import { IAction } from '@Root/types'

import {
  IProjectVersion,
  IProjectVersionReference,
  IProjectVersionData,
} from '@SiteModules/ProjectVersion/types'

import {
  ISubtitleData,
} from '@SiteModules/Subtitle/types'

export const ProjectActionTypes = {
  FETCH_PROJECTS: '@@project/FETCH_PROJECTS',
  FETCH_PROJECTS_SUCCESS: '@@project/FETCH_PROJECTS_SUCCESS',
  FETCH_PROJECTS_FAILURE: '@@project/FETCH_PROJECTS_FAILURE',
  CHANGE_SELECTED_PROJECTS: '@@project/CHANGE_SELECTED_PROJECTS',
  UNSELECT_PROJECTS: '@@project/UNSELECT_PROJECTS',
  RESET_SHOULD_CHECK_AUTO_FILL_LIST: '@@project/RESET_SHOULD_CHECK_AUTO_FILL_LIST',
  RESET_SHOULD_CHECK_AUTO_FILL_GRID: '@@project/RESET_SHOULD_CHECK_AUTO_FILL_GRID',
  SET_SHOULD_CHECK_AUTO_FILL: '@@project/SET_SHOULD_CHECK_AUTO_FILL',
  SET_LIST_ACTIVE: '@@project/SET_LIST_ACTIVE',
  SET_GRID_ACTIVE: '@@project/SET_GRID_ACTIVE',
  CREATE_PROJECT: '@@project/CREATE_PROJECT',
  CREATE_PROJECT_SUCCESS: '@@project/CREATE_PROJECT_SUCCESS',
  CREATE_PROJECT_FAILURE: '@@project/CREATE_PROJECT_FAILURE',
  DELETE_PROJECTS: '@@project/DELETE_PROJECTS',
  DELETE_PROJECTS_FAILURE: '@@project/DELETE_PROJECTS_FAILURE',
  DELETE_PROJECTS_SUCCESS: '@@project/DELETE_PROJECTS_SUCCESS',
  SET_SEARCH_TEXT: '@@project/SET_SEARCH_TEXT',
  UPDATE_PROJECTS: '@@project/UPDATE_PROJECTS',
  UPDATE_PROJECTS_SUCCESS: '@@project/UPDATE_PROJECTS_SUCCESS',
  UPDATE_PROJECTS_FAILURE: '@@project/UPDATE_PROJECTS_FAILURE',
  UPDATE_PROJECT_TITLE: '@@project/UPDATE_PROJECT_TITLE',
  UPDATE_PROJECT_TITLE_SUCCESS: '@@project/UPDATE_PROJECT_TITLE_SUCCESS',
  UPDATE_PROJECT_TITLE_FAILURE: '@@project/UPDATE_PROJECT_TITLE_FAILURE',
  CREATE_CLIPS: '@@project/CREATE_CLIPS',
  CREATE_CLIPS_SUCCESS: '@@project/CREATE_CLIPS_SUCCESS',
  CREATE_CLIPS_FAILURE: '@@project/CREATE_CLIPS_FAILURE',
  START_BULK_EXPORTATION: '@@project/START_BULK_EXPORTATION',
  START_BULK_EXPORTATION_SUCCESS: '@@project/START_BULK_EXPORTATION_SUCCESS',
  START_BULK_EXPORTATION_FAILURE: '@@project/START_BULK_EXPORTATION_FAILURE',
}

export interface IProjectState {
  readonly projects: IProject[]
  readonly hasLoadedInitially: boolean
  readonly loading: boolean
  readonly deleting: boolean
  readonly updating: boolean
  readonly startingBulkExportation: boolean
  readonly selectedProjectIds: string[]
  readonly searchText: string
  readonly page: {
    readonly listActive: boolean
    readonly currentPage: number
    readonly totalCount: number
    readonly totalPages: number
    readonly shouldCheckAutoFillGrid: boolean
    readonly shouldCheckAutoFillList: boolean
  }
}

export interface IProject {
  readonly id: string
  readonly attributes: IProjectAttributes
  readonly projectVersions: IProjectVersion[]
}

export interface IProjectData {
  readonly id: string
  readonly type: 'project'
  readonly attributes: IProjectAttributes
  readonly relationships: IProjectRelationships
}

export interface IProjectAttributes {
  readonly title: string
  readonly createdAt: string
  readonly mostRecentUpdatedAtTimestamp: number
  readonly canManageProjectSharing: boolean
}

export interface IProjectRelationships {
  readonly projectVersions: {
    readonly data: IProjectVersionReference[]
  }
}

export type IncludedResource = IProjectVersionData | ISubtitleData

export interface IFetchProjectsSuccessResponseBody {
  readonly data: IProjectData[]
  readonly included: IncludedResource[]
  readonly totalCount: number
  readonly totalPages: number
  readonly currentPage: number
}

export interface IFetchProjectsPayload {
  readonly workspaceId: number
  readonly selectedLabels: number[]
  readonly searchText?: string
  readonly page: number
}

export interface IChangeSelectedProjectsPayload {
  readonly projectId: string
  readonly selectProject: boolean
}

export interface ICreateProjectSuccessResponseBody {
  readonly data: IProjectData[]
}

export interface ICreateProjectPayload {
  readonly workspaceId: number
}

export interface IDeleteProjectsSuccessPayload {
  readonly projectIds: string[]
}

export interface IDeleteProjectsPayload extends IDeleteProjectsSuccessPayload {
  readonly notifyAboutSuccess?: boolean
}

export interface ISetSearchTextPayload {
  readonly newSearchText: string
}

export interface IUpdateProjectsPayload {
  readonly subtitlesToUpdateIds: number[]
  readonly projectVersionsToUpdateIds: string[]
  readonly selectedLabels: number[]
  readonly workspaceId: number
}

export interface IUpdateProjectsSuccessResponseBody {
  readonly subtitles: {
    readonly data: ISubtitleData[]
  }
  readonly projectVersions: {
    readonly data: IProjectVersionData[]
  }
}

export interface IProjectLabelCaptions {
  projectId: string
  captions: string[]
}

export interface IUpdateProjectTitlePayload {
  projectId: string
  updatedTitle: string
}

export interface ITranslationSettings {
  readonly language: string
  readonly translationGuidelineId?: number
  readonly translationGuidelineOption?: string | null
  readonly srtTranslationText?: string
  readonly scriptFileName?: string | null
}

export interface ISetupProjectBasePayload {
  readonly projectVersionLanguage: string
  readonly formality?: string
  readonly translationSettings?: ITranslationSettings[]
  readonly termbaseId?: number
  readonly themeId?: number
  readonly repurposeInstructions?: string
}

export interface ICreateClipsPayload extends ISetupProjectBasePayload {
  readonly projectId: string
  readonly outputFormat: string
  readonly reviewTranscriptBeforeTranslation?: boolean
}

export type IUpdateProjectTitleSuccessPayload = IUpdateProjectTitlePayload

export interface IStartBulkExportationPayload {
  readonly projectVersionIdsToExportWithoutSubtitles: string[]
  readonly subtitleIdsToExportAsSubtitles: number[]
  readonly subtitleIdsToExportAsAudiosAndVideos: number[]
  readonly selectedFormats: string[],
  readonly shouldHaveOneAudioFilePerSpeaker: boolean
  readonly shouldBurnInSubtitles: boolean
}

export interface IProjectSharingUser {
  readonly id: number
  readonly email: string
  readonly name: string
  readonly lastName?: string
}

export interface IProjectSharingResponseBody {
  readonly sharingMode: 'private' | 'workspace'
  readonly ownerUserId?: number
  readonly projectUserIds: number[]
  readonly users: IProjectSharingUser[]
  readonly workspaceUsers: IProjectSharingUser[]
}

export interface IFetchProjectSharingPayload {
  readonly projectId: string
}

export interface IUpdateProjectSharingPayload {
  readonly projectId: string
  readonly sharingMode: 'private' | 'workspace'
  readonly userIds: number[]
}

export interface IUpdateBulkProjectSharingPayload {
  readonly projectIds: string[]
  readonly sharingMode: 'private' | 'workspace'
  readonly userIds: number[]
}

export type TProjectActionTypes =
  IAction<IFetchProjectsSuccessResponseBody> |
  IAction<IChangeSelectedProjectsPayload> |
  IAction<ISetSearchTextPayload> |
  IAction<IDeleteProjectsSuccessPayload> |
  IAction<IUpdateProjectsSuccessResponseBody> |
  IAction<IUpdateProjectTitleSuccessPayload>
